import type { ReactNode } from 'react';
import clsx from 'clsx';
import { Icon, type IconName } from './Icon';
import { useSidebarCollapse } from './sidebar-collapse';

export type NavItem = {
  id: string;
  label: string;
  icon: IconName;
};

export type SidebarProps = {
  items: NavItem[];
  activeId?: string;
  onNavigate?: (id: string) => void;
  onSignOut?: () => void;
  /** Replace the brand area entirely (e.g. for a real logo). */
  brand?: ReactNode;
  /**
   * When provided, renders the search field as the first item below the brand
   * (Figma node 1061:63364). Presentational by default — wire `onSearchChange`
   * to filter.
   */
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
};

const suvodaLogoPaths = [
  {
    inset: '3.18% 16.3% 6.68% 69.55%',
    viewBox: '0 0 14.1478 11.2672',
    fill: '#412C59',
    d: 'M0.702606 11.2475C0.446606 11.2475 0.2438 11.2475 0 11.2475C0 7.50006 0 3.77798 0 0.0540356C0.0277 0.0353433 0.0559021 -0.000220127 0.0837021 1.02663e-06C2.7311 0.0208443 5.38231 -0.0265009 8.02451 0.0926135C9.80001 0.172653 11.4236 0.783275 12.6628 2.02507C15.4892 4.85719 14.101 9.5039 10.0214 10.7228C8.9657 11.0383 7.8172 11.1808 6.7029 11.2253C4.7239 11.3043 2.73851 11.2475 0.702606 11.2475ZM8.3703 2.42364C6.5286 2.02143 4.6646 2.25552 2.816 2.18539C2.816 4.50547 2.816 6.75029 2.816 8.92054C4.4947 8.92054 6.1473 9.02317 7.7769 8.89025C9.2594 8.76933 10.5361 8.12758 11.0123 6.70085C11.5254 5.16338 11.1661 3.18768 8.3703 2.42364Z',
  },
  {
    inset: '0 33.37% 3.85% 51%',
    viewBox: '0 0 15.6373 12.0192',
    fill: '#412B58',
    d: 'M14.8975 8.90195C14.0752 10.2402 12.8466 11.0809 11.3134 11.5161C8.95587 12.1853 6.57467 12.2025 4.22547 11.4741C1.01957 10.4802 -0.302531 8.02478 0.0575687 5.19359C0.387869 2.59701 2.10287 1.0604 4.76607 0.386755C6.91207 -0.156073 9.08847 -0.129295 11.2234 0.478509C14.258 1.34248 15.9543 3.69365 15.588 6.58758C15.4902 7.36083 15.1446 8.10783 14.8975 8.90195ZM11.4497 8.75641C13.6652 6.91038 13.0262 3.58706 10.2432 2.64342C8.62817 2.09585 6.96537 2.10675 5.35257 2.66123C3.82067 3.18788 2.98387 4.24901 2.90447 5.75143C2.81447 7.45337 3.54687 8.70226 5.13467 9.27095C7.27077 10.0361 9.39847 10.012 11.4497 8.75641Z',
  },
  {
    inset: '2.97% 0 6.81% 83.96%',
    viewBox: '0 0 16.0419 11.2776',
    fill: '#412C58',
    d: 'M1.2438 9.10528C2.9357 6.18958 4.6137 3.3096 6.2654 0.417017C6.4502 0.0934943 6.6506 -0.0120336 7.0304 0.00106924C7.7704 0.026599 8.5126 -0.00503171 9.2527 0.0190356C9.4173 0.0243914 9.6564 0.123434 9.7272 0.244822C11.8412 3.86989 13.9385 7.50317 16.0362 11.1362C16.0507 11.1605 16.0336 11.1998 16.0283 11.2728C15.1108 11.2728 14.1912 11.2848 13.2727 11.2596C13.14 11.2559 12.9668 11.0969 12.8917 10.9703C12.5568 10.4056 12.273 9.81476 11.9206 9.26005C11.8163 9.0957 11.5496 8.91876 11.355 8.91664C9.1509 8.89257 6.9463 8.89278 4.7421 8.9162C4.5473 8.91827 4.2794 9.08854 4.1755 9.25053C3.8204 9.8039 3.53911 10.3966 3.19341 10.9554C3.10561 11.0973 2.8865 11.2536 2.7219 11.2593C1.853 11.2894 0.9822 11.2734 0 11.2734C0.4283 10.5248 0.825305 9.83101 1.2438 9.10528ZM7.4595 6.79548C8.4642 6.79548 9.4689 6.79548 10.5547 6.79548C9.694 5.24881 8.8801 3.78629 8.0299 2.25855C7.1692 3.81366 6.3579 5.2795 5.5321 6.77148C5.6632 6.78596 5.6999 6.79345 5.7365 6.79354C6.2739 6.79476 6.8112 6.795 7.4595 6.79548Z',
  },
  {
    inset: '3.27% 66.25% 3.99% 19.98%',
    viewBox: '0 0 13.7764 11.5928',
    fill: '#412C59',
    d: 'M13.7699 6.82954C13.2838 9.85992 10.9256 11.2955 7.94555 11.5423C6.53605 11.6591 5.12945 11.5937 3.78665 11.1274C1.52355 10.3416 0.204253 8.81585 0.0690535 6.62623C-0.0664465 4.43354 0.039253 2.22836 0.039253 0C0.946053 0 1.84605 0 2.81695 0C2.82585 0.166038 2.84345 0.342932 2.84375 0.519845C2.84595 2.24874 2.83275 3.97773 2.84865 5.7065C2.86805 7.82333 3.99695 9.03428 6.24275 9.27031C6.90845 9.34021 7.62055 9.28935 8.27515 9.1507C10.0365 8.77779 10.9802 7.56814 10.9911 5.77532C11.0016 4.04648 10.9936 2.31754 10.9936 0.588643C10.9937 0.404199 10.9937 0.219751 10.9937 0.00325192C11.9276 0.00325192 12.8257 0.00325192 13.7764 0.00325192C13.7764 2.26014 13.7764 4.52187 13.7699 6.82954Z',
  },
  {
    inset: '2.98% 49.41% 6.75% 35.22%',
    viewBox: '0 0 15.3664 11.2847',
    fill: '#412C59',
    d: 'M10.2793 3.80145C10.9042 2.62833 11.5142 1.49053 12.1041 0.344077C12.2313 0.096865 12.3798 -0.00720811 12.6904 0.000386121C13.5537 0.0214909 14.418 0.00771113 15.3664 0.00771113C15.0949 0.49853 14.855 0.930712 14.6166 1.36364C12.8797 4.51872 11.1374 7.67134 9.4148 10.833C9.2329 11.167 9.031 11.3041 8.616 11.2825C7.9332 11.247 7.2453 11.2938 6.5624 11.2593C6.3716 11.2495 6.1 11.1186 6.0184 10.972C4.0406 7.41705 2.0835 3.85251 0.123699 0.289206C0.0839992 0.216967 0.0571 0.138787 0 0.00761786C0.9922 0.00761786 1.9291 -0.00960713 2.8638 0.0252823C3.0123 0.0308275 3.2009 0.248019 3.2858 0.405772C4.6815 2.99833 6.0632 5.59714 7.4491 8.19408C7.5193 8.3256 7.5988 8.45291 7.7061 8.63727C8.5798 6.99217 9.4185 5.41293 10.2793 3.80145Z',
  },
  {
    inset: '2.99% 81.94% 6.77% 0',
    viewBox: '0 0 18.0643 11.2796',
    fill: '#412C58',
    d: 'M13.225 0.412904C14.822 -0.105707 16.4317 0.00820952 18.0643 0.0156076C18.0643 0.751049 18.0643 1.45384 18.0643 2.22544C17.5765 2.22544 17.1008 2.23299 16.6255 2.22413C14.7225 2.18861 13.2393 2.93726 12.0646 4.26262C10.8054 5.68338 9.55725 7.11236 8.29091 8.52773C6.59891 10.4188 4.43085 11.3693 1.73905 11.2729C1.20281 11.2538 0.665178 11.2675 0.12821 11.2637C0.0952276 11.2634 0.062426 11.2419 0 11.22C0 10.5246 0 9.8199 0 9.0468C0.426049 9.0468 0.862513 9.03746 1.29837 9.0484C3.2095 9.0964 4.74299 8.41913 5.93801 7.06125C7.23837 5.58367 8.54148 4.10795 9.86181 2.64535C10.7598 1.65065 11.8465 0.877059 13.225 0.412904Z',
  },
] as const;

function SuvodaWordmark() {
  return (
    <span aria-label="Suvoda" className="relative block h-[12.5px] w-[100px] overflow-hidden">
      <span aria-hidden="true" className="absolute inset-[0_0_3.85%_0]">
        {suvodaLogoPaths.map((logoPath) => (
          <span key={logoPath.viewBox} className="absolute" style={{ inset: logoPath.inset }}>
            <svg
              className="block h-full w-full"
              viewBox={logoPath.viewBox}
              preserveAspectRatio="none"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d={logoPath.d} fill={logoPath.fill} />
            </svg>
          </span>
        ))}
      </span>
    </span>
  );
}

// Horizontal padding is applied per-state by the caller (expanded: pl-4 pr-3,
// collapsed: px-0 + centered) so collapsed icons truly center without relying on
// Tailwind class ordering to override the padding.
const navItemBase = clsx(
  'flex h-[34px] items-center gap-[6px] cursor-pointer no-underline',
  'rounded-md',
  // Design review #8: muted nav items — text-secondary (#67738E), 12px, regular.
  'text-text-secondary font-normal text-sm',
  'transition-[background-color] duration-fast',
  'hover:bg-ink-100',
  'aria-[current=page]:bg-[#eeeef0] aria-[current=page]:font-medium aria-[current=page]:text-text-primary',
);

export function Sidebar({
  items,
  activeId,
  onNavigate,
  onSignOut,
  brand,
  searchPlaceholder,
  searchValue,
  onSearchChange,
}: SidebarProps) {
  // F-043: collapse to an icons-only rail; the width animates so the bar slides.
  const { collapsed } = useSidebarCollapse();
  return (
    <aside
      className={clsx(
        'sticky top-0 flex h-screen shrink-0 flex-col self-stretch overflow-hidden border-r border-border bg-surface-sidebar',
        'transition-[width] duration-slow ease-standard motion-reduce:transition-none',
        collapsed ? 'w-16' : 'w-sidebar',
      )}
    >
      <div
        className={clsx(
          'flex h-12 items-start border-b border-border pt-[17.25px]',
          collapsed ? 'justify-center pl-0' : 'pl-[23px]',
        )}
      >
        {!collapsed && (brand ?? <SuvodaWordmark />)}
      </div>
      {!collapsed && searchPlaceholder !== undefined && (
        // Figma 1061:63364: search field as the first sidebar item.
        <div className="flex h-9 items-center border-b border-border bg-surface-chip px-[21px]">
          <input
            type="search"
            aria-label="Search"
            className="h-[18px] w-full border-0 bg-transparent p-0 text-sm text-text-primary outline-none placeholder:text-text-secondary"
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(event) => onSearchChange?.(event.target.value)}
          />
        </div>
      )}
      <nav aria-label="Primary" className="mx-2 mt-2 flex flex-col gap-1">
        {items.map((it) => {
          const isActive = it.id === activeId;
          return (
            <a
              key={it.id}
              href="#"
              aria-current={isActive ? 'page' : undefined}
              // Collapsed: the text label is hidden, so carry it as the accessible
              // name + a hover/focus tooltip (FR-005). Expanded: the visible text is
              // the accessible name, so no override is needed.
              aria-label={collapsed ? it.label : undefined}
              title={collapsed ? it.label : undefined}
              onClick={(e) => {
                e.preventDefault();
                onNavigate?.(it.id);
              }}
              className={clsx(navItemBase, collapsed ? 'justify-center px-0' : 'pl-4 pr-3')}
            >
              <span className={clsx('text-text-secondary', isActive && 'text-text-primary')}>
                <Icon name={it.icon} size={16} />
              </span>
              {!collapsed && it.label}
            </a>
          );
        })}
      </nav>
      <div className="flex-1" />
      <button
        type="button"
        onClick={onSignOut}
        aria-label={collapsed ? 'Sign Out' : undefined}
        title={collapsed ? 'Sign Out' : undefined}
        className={clsx(
          'flex h-[43px] w-full cursor-pointer items-center gap-[6px] border-t border-border text-sm text-text-secondary hover:text-text-primary',
          collapsed ? 'justify-center px-0' : 'pl-4 pr-3',
        )}
      >
        <Icon name="power" size={16} />
        {!collapsed && 'Sign Out'}
      </button>
    </aside>
  );
}
